import React from "react";
import { Card, Icon, Label, Popup } from "semantic-ui-react";
import { formatCurrency } from "./math";
import { getAltPayFlag } from "../../../utils/doctorPayHelpers";
import { isBlankNullOrUndefined } from '../../../utils/validations';
import { mergeObjects, groupBy, calculateVisitsPerDay } from './dataTransformations';

export const percentageBillableAllowedContracts = new Set([
    'Greater Of', 'TMPDOC', 'TMPHYN', 'Collections', 'GOX', 'GOZ',
    'Sum Of', 'UKG HYN', 'Greater Of w/HYN', 'Sum Of w/HYN',
    'Collections w/HYN', 'GOX w/HYN', 'Prod Hurdle', 'Collections Offset', 'GO GA', 'GO GA w/HYN'
]);

export const toArray = (v) => {
    if (v === null || v === undefined) return null;
    if (Array.isArray(v)) return v.length ? v : null;
    if (typeof v === 'string') {
        const t = v.trim();
        return t.length ? [t] : null;
    }
    return [v];
};

export const filterByAltPay = (item) => {
    const altPayExists = typeof (item.doctorPayVariables?.altPay) !== 'undefined';
    return getAltPayFlag(item) === false || !altPayExists;
};

export const filterByPracticeAndPositiveValues = (item) => {
    if (!item) return false;
    const hasPractice = Array.isArray(item.doctorPracticesArray) &&
        item.doctorPracticesArray.some(practice => practice && practice.trim() !== '');
    if (!hasPractice) return false;

    return (item.price > 0) || (item.bCare > 0) || (item.totalPay > 0) ||
        (item.financialNotes > 0) || (item.procBasedPay > 0);
};

export const applyAccountCalculations = (item, filtersAccount, sageAccountUkg) => {
    const newItem = { ...item };
    if (filtersAccount && filtersAccount.length > 0) {
        const practicesObj = newItem.data || {};
        let totalCalculatedPercBillable = 0;

        // Recalculate financialNotes to only include selected accounts
        let filteredFinNotes = 0;
        if (Array.isArray(newItem.financialNoteList)) {
            newItem.financialNoteList.forEach(note => {
                if (filtersAccount.includes(note.sageAccount)) {
                    filteredFinNotes += Number(note.financialNotes) || 0;
                }
            });
        }
        newItem.financialNotes = filteredFinNotes;

        if (Object.keys(newItem.sageAccOfBillable || {}).length > 0) {
            let totalSelectedAccountNotes = 0;
            Object.values(practicesObj).forEach(pracItem => {
                if (!pracItem) return;
                const payNegFinNotes = (pracItem.procBasedPay || 0) - (pracItem.financialNotes || 0);
                let totalFinNotes = 0;
                Object.entries(pracItem.financialNotesByAccount || {}).forEach(([acc, amount]) => {
                    if (filtersAccount.includes(acc)) {
                        totalFinNotes += amount || 0;
                    }
                });
                totalSelectedAccountNotes += totalFinNotes;
                totalCalculatedPercBillable += (newItem.bCare) ? ((payNegFinNotes + totalFinNotes) / newItem.bCare) * 100 : 0;
            });
            newItem.percBillableByAccount = totalCalculatedPercBillable;
            newItem.accountProcBasedPay = (newItem.payMinusFinNotes || 0) + totalSelectedAccountNotes;
        } else {
            let sumFinNotes = 0;
            Object.entries(newItem.finNotesByAccount || {}).forEach(([acc, amount]) => {
                if (filtersAccount.includes(acc)) {
                    sumFinNotes += amount || 0;
                }
            });
            totalCalculatedPercBillable += (newItem.bCare) ? (sumFinNotes / newItem.bCare) * 100 : 0;
            newItem.percBillableByAccount = totalCalculatedPercBillable;
            newItem.accountProcBasedPay = (newItem.payMinusFinNotes || 0) + sumFinNotes;
        }
    } else {
        newItem.percBillableByAccount = newItem.percentageOfBillable ?? 0;
        newItem.accountProcBasedPay = newItem.payMinusFinNotes;
    }
    newItem.financialNoteList?.forEach(note => note.ukgCode = sageAccountUkg[note.accountNumber]);
    return newItem;
};

const sumOfContracts = new Set(['Sum Of', 'Sum Of w/HYN']);
const otherContracts = new Set(['Collections', 'GOX', 'GOZ', 'Greater Of', 'Greater Of w/HYN', 'Collections w/HYN', 'GOX w/HYN', 'Collections Offset']);

// Single source of truth for which "base pay tracked" bucket a contract type falls into.
// Anything not in either bucket (Hygienist Incentive, Prod Hurdle, UKG HYN, etc.) is not
// tracked for Base / Over Base purposes at all.
// NOTE: the second bucket is deliberately NOT labeled "Other" — this codebase already uses
// "Other Practice Making Base" to mean a *different, multi-practice* concept (one practice
// over base, another not). "Base Threshold" here just names the group of contract types
// whose Base/Over Base comes from comparing pay against a base-pay threshold (as opposed to
// Sum Of's bonus-eligibility flag) — it says nothing about how many practices are involved.
export const getProviderBaseGroup = (contractType) => {
    if (sumOfContracts.has(contractType)) return 'Sum Of';
    if (otherContracts.has(contractType)) return 'Base Threshold';
    return null;
};

// Raw over-base signal for a tracked contract type, before the payType/base-configured/
// worked-days gates below are applied. Shared by isProviderOverBase and getProviderBaseAuditInfo
// so the two can never drift apart again.
const computeRawOverBaseSignal = (el, group) => {
    if (group === 'Sum Of') return !!el.isProviderEligibleForBonus;
    // Derive the signal from the figures the row displays, not from the stored
    // isProviderOverBase flag, so a snapshot saved before a pay-logic fix cannot keep a stale
    // classification. Base = the larger of the summed per-practice daily-rate base and the
    // provider's displayed Base Pay: for Category B (prorated monthly base) the displayed
    // figure is authoritative, for every other category the two are equal or the summed
    // figure is, so only Category B changes. Cents-rounded with a one-cent tolerance so a pay
    // that equals base (15750.0000001 vs 15750) is never "over base".
    const hasFigures = el.payMinusFinNotes != null && (el.totalBasePay != null || el.displayBasePay != null);
    if (hasFigures) {
        const base = Math.max(Number(el.totalBasePay) || 0, Number(el.displayBasePay) || 0);
        return Math.round((Number(el.payMinusFinNotes) || 0) * 100) - Math.round(base * 100) > 1;
    }
    return !!el.isProviderOverBase;
};

export const isProviderOverBase = (el, payType = undefined) => {
    const group = getProviderBaseGroup(el.contractType);
    // Providers whose Base Pay column displays "-" (displayBasePay = 0) have no configured
    // base pay and should not appear in Base or Over Base filter buckets. Using totalBasePay
    // is incorrect here because for contracts like "Greater Of w/HYN" the daily-rate-based
    // totalBasePay can be > 0 even when monthlyBasePay = 0 (shown as "-" in the UI).
    if (payType !== undefined && !(el.displayBasePay > 0)) return false;
    let status = computeRawOverBaseSignal(el, group);
    if (payType !== undefined) {
        status = !!status === !!payType;
    }
    return !!group && status && +el.numberOfWorkedDays;
};

// Full audit breakdown for a single provider row: the contract-type bucket it falls into,
// the raw child-level classification, and the final status as actually counted by the
// Base Pay badge (isProviderOverBase(el, false) === true means "counted as Base").
export const getProviderBaseAuditInfo = (el) => {
    const group = getProviderBaseGroup(el.contractType);
    if (!group) {
        return { group: 'Not Tracked', childStatus: 'Not Tracked', finalStatus: 'Not Tracked' };
    }
    // childStatus intentionally reuses isProviderOverBase(el) with no payType — the exact
    // same call renderTotalPayLabel() makes to drive the Total Pay label's pointing/
    // borderYellow on the CDP Reports table itself — so this matches what that report is
    // actually showing for this provider, not just the raw pre-gate signal.
    const childStatus = isProviderOverBase(el) ? 'Over Base' : 'Base';
    if (!(el.displayBasePay > 0)) {
        return { group, childStatus, finalStatus: 'No Base Configured' };
    }
    if (!+el.numberOfWorkedDays) {
        return { group, childStatus, finalStatus: 'No Worked Days' };
    }
    return { group, childStatus, finalStatus: isProviderOverBase(el, false) ? 'Base' : 'Over Base' };
};

// A label only claims "on base" (points left, yellow border) for a provider the Base Pay
// filter and counter also track: base configured and days worked. Keeps label, filter and
// header counter in agreement.
const isRowOnBase = (row) => !!(row.displayBasePay > 0) && !!+row.numberOfWorkedDays && !isProviderOverBase(row);

const contractLabelConfig = {
    'Hygienist Incentive': {
        color: 'green',
        className: 'cursorPointer',
    },
    'Collections': {
        pointing: row => isProviderOverBase(row) ? 'right' : 'left',
        className: row => ` ${isRowOnBase(row) && 'borderYellow'} bg-color-collections cursorPointer`,
    },
    'Collections w/HYN': {
        pointing: row => isProviderOverBase(row) ? 'right' : 'left',
        className: row => ` ${isRowOnBase(row) && 'borderYellow'} bg-color-collections cursorPointer`,
    },
    'Sum Of': {
        pointing: row => isProviderOverBase(row) ? 'right' : 'left',
        className: row => ` ${isRowOnBase(row) && 'borderYellow'} cursorPointer primaryColor borderPrimary`,
    },
    'Sum Of w/HYN': {
        pointing: row => isProviderOverBase(row) ? 'right' : 'left',
        className: row => ` ${isRowOnBase(row) && 'borderYellow'} cursorPointer primaryColor borderPrimary`,
    },
    'Greater Of': {
        pointing: row => isProviderOverBase(row) ? 'right' : 'left',
        className: row => ` ${isRowOnBase(row) && 'borderYellow'} cursorPointer bg-color-primary color-white`,
    },
    'Greater Of w/HYN': {
        pointing: row => isProviderOverBase(row) ? 'right' : 'left',
        className: row => ` ${isRowOnBase(row) && 'borderYellow'} cursorPointer bg-color-primary color-white`,
    },
    'GOZ': {
        pointing: row => isProviderOverBase(row) ? 'right' : 'left',
        className: row => `${isRowOnBase(row) && 'borderYellow'}`,
        style: { color: 'black', backgroundColor: '#FF00FF' },
    },
    'GOX': {
        pointing: row => isProviderOverBase(row) ? 'right' : 'left',
        className: row => `${isRowOnBase(row) && 'borderYellow'}`,
        style: { color: 'black', backgroundColor: '#21BA45' },
    },
    'GOX w/HYN': {
        pointing: row => isProviderOverBase(row) ? 'right' : 'left',
        className: row => `${isRowOnBase(row) && 'borderYellow'}`,
        style: { color: 'black', backgroundColor: '#5FE47F' },
    },
    'Collections Offset': {
        pointing: row => isProviderOverBase(row) ? 'right' : 'left',
        className: row => ` ${isRowOnBase(row) && 'borderYellow'} bg-color-collections cursorPointer`,
    },
    'GO GA': {
        className: 'cursorPointer',
        style: { color: 'white', backgroundColor: '#E65100' },
    },
    'GO GA w/HYN': {
        className: 'cursorPointer',
        style: { color: 'white', backgroundColor: '#E65100' },
    },
    'Prod Hurdle': {
        style: { color: 'black', backgroundColor: '#21BA45' },
    },
    'UKG HYN': {
        className: 'cursorPointer primaryColor',
        useFinancialNotes: true,
    },
    'TMPDOC': {
        className: 'cursorPointer primaryColor',
        useFinancialNotes: true,
    },
    'TMPHYN': {
        className: 'cursorPointer primaryColor',
        useFinancialNotes: true,
    },
};

const TeamPopUp = ({ teamContribution }) => {
    if (!teamContribution) return <></>;
    const teamAmount = `${teamContribution > 0 ? '+' : ''}${formatCurrency(teamContribution)}`
    const teamContentCard = <Card
        meta="Team Contribution"
        description={<b className={teamContribution > 0 ? 'text-green' : 'text-red'}>{teamAmount}</b>}
    />
    const teamTrigger = <Icon name="users" color="yellow" className="ml-2 mr-0" />;
    return <Popup trigger={teamTrigger} content={teamContentCard} position="top center" />;
}

/**
 * Identifies which practices for a provider have oral surgery shifts in the schedule.
 */
export const getOralSurgeryPractices = (provider, scheduleHours, getPracticeName) => {
    const osPractices = [];
    const practices = provider.data ? Object.keys(provider.data) : [];
    const providerAbbr = (provider.ABBR || '').toLowerCase();
    practices.forEach(practice => {
        const practiceName = (getPracticeName(practice) || practice || '').toLowerCase();
        const shifts = scheduleHours[practiceName] || [];
        const hasOralSurgery = shifts.some(shift =>
            shift.shiftName && shift.shiftName.toLowerCase().includes('oral surg') &&
            shift.abbr && shift.abbr.toLowerCase().includes(providerAbbr)
        );
        if (hasOralSurgery) {
            osPractices.push(practice);
        }
    });
    return osPractices;
};

/**
 * Recalculates provider-level aggregates from a filtered subset of practice data.
 * Used when the role filter restricts which practices should count for a provider.
 * @param {object} item - the original provider row
 * @param {object} dataObj - the filtered practice data keyed by practice id
 * @param {function} calculateBCare - prop function for billable care calculation
 */
export const recalculateFromPractices = (item, dataObj, calculateBCare) => {
    const practices = Object.values(dataObj);
    const originalPracticeCount = Object.keys(item.data || {}).length;
    const filteredPracticeCount = Object.keys(dataObj || {}).length;
    const isSubsetOfPractices = filteredPracticeCount !== originalPracticeCount;
    const sumProp = (prop) => practices.reduce((sum, p) => sum + (parseFloat(p[prop]) || 0), 0);
    const newCare = sumProp('price');
    const newAdj = sumProp('adjustmentPrice');
    const newWO = sumProp('writeOffPrice');
    const newProcBasedPay = sumProp('practiceTotalPay');
    const newBCare = sumProp('practiceBcare');
    const newBillableNotes = sumProp('billableNotes');
    const newFinancialNotes = sumProp('financialNotes');
    const newPatientVisits = sumProp('patientVisits');
    const newHynBillableCare = sumProp('hynBillableCare');
    const newTotalBasePay = sumProp('practicePayBasedOnDailyRate');
    const newWorkedDays = sumProp('worked');
    const newScheduledDays = sumProp('scheduled');
    // GoxNonBillableCare backs the "Gx,Gz,Col" column; the backend accumulates it from a
    // different per-practice source depending on contract type (reportsServiceFunctions.js).
    const newGoxNonBillableCare = ['GOX', 'GOZ', 'GOX w/HYN'].includes(item.contractType)
        ? sumProp('goxNonBillableCare')
        : ['Collections', 'Collections w/HYN', 'Collections Offset'].includes(item.contractType)
            ? sumProp('collectionsNonBillableCare')
            : 0;
    const newBCPV = newPatientVisits !== 0 ? parseFloat((newBCare / newPatientVisits) || 0) : 0;
    const newPatientVisitsPerDay = calculateVisitsPerDay(newPatientVisits, newWorkedDays);
    const newNormalizedBillableCare = calculateBCare
        ? calculateBCare(newCare, newAdj, newWO, 90)
        : item.normalizedBillableCare;
    const newPayMinusFinNotes = newProcBasedPay - newFinancialNotes;
    const newNormalizedPct = newNormalizedBillableCare > 0
        ? (100 * newProcBasedPay / (newNormalizedBillableCare + newBillableNotes))
        : 0;
    const newPctOfBillable = newBCare > 0 ? (newProcBasedPay / newBCare) * 100 : 0;

    // Frequency-based deposit split — mirrors ADP export logic.
    // Notes with 'first'/'second' go fully to that deposit; 'split' goes half each;
    // notes with no frequency stay in base pay and are split 50/50.
    let _totalValidNotes = 0, _firstNotes = 0, _secondNotes = 0;
    practices.forEach(p => {
        (p.financialNoteList || []).forEach(fn => {
            const freq = (fn.frequency || '').toLowerCase();
            const amt = parseFloat(fn.financialNotes) || 0;
            if (freq === 'first') {
                _totalValidNotes += amt; _firstNotes += amt;
            } else if (freq === 'second') {
                _totalValidNotes += amt; _secondNotes += amt;
            } else if (freq === 'split') {
                _totalValidNotes += amt; _firstNotes += amt / 2; _secondNotes += amt / 2;
            }
        });
    });
    const _baseForDeposit = newProcBasedPay - _totalValidNotes;

    return {
        ...item,
        procBasedPay: newProcBasedPay,
        bCare: newBCare,
        price: newCare,
        writeOffPrice: newWO,
        adjustmentPrice: newAdj,
        billableNotes: newBillableNotes,
        financialNotes: newFinancialNotes,
        patientVisits: newPatientVisits,
        numberOfWorkedDays: newWorkedDays,
        numberOfScheduledDays: newScheduledDays,
        totalScheduledDays: newScheduledDays,
        bCPV: newBCPV,
        patientVisitsPerDay: newPatientVisitsPerDay,
        GoxNonBillableCare: newGoxNonBillableCare,
        hynBillableCare: newHynBillableCare,
        normalizedBillableCare: newNormalizedBillableCare,
        normalizedPercentageOfBillableCare: newNormalizedPct,
        percentageOfBillable: newPctOfBillable,
        payMinusFinNotes: newPayMinusFinNotes,
        totalBasePay: newTotalBasePay,
        displayBasePay: !isSubsetOfPractices && item.displayBasePay !== undefined && item.displayBasePay !== null
            ? item.displayBasePay
            : (item.contractType === 'Greater Of w/HYN'
                || (item.contractType === 'Greater Of' && (item.greater_of_category && item.greater_of_category.toUpperCase() !== 'NOT APPLICABLE' ? item.greater_of_category : 'A').toUpperCase() !== 'A')
                || (['GOZ', 'GOX', 'GOX w/HYN'].includes(item.contractType) && (item.greater_of_category && item.greater_of_category.toUpperCase() !== 'NOT APPLICABLE' ? item.greater_of_category : 'A').toUpperCase() !== 'A'))
                && item.displayBasePay > 0
                ? item.displayBasePay
                : newTotalBasePay,
        firstDeposit: _baseForDeposit / 2 + _firstNotes,
        secondDeposit: _baseForDeposit / 2 + _secondNotes,
    };
};

/**
 * Shared "strict filter" engine: any filter that maps to a subset of a doctor's
 * practices (Practice, Region, Brand, ...) uses this to narrow item.data down to
 * the matching practices and recompute totals from just that subset, instead of
 * just deciding row visibility. Used by filterTableData in both CdpReportsTableByDentists
 * (CdpReports and CdpReportsAlt).
 *
 * @param {Array} data - rows to filter (each a doctor-level row with a `data` map keyed by practice)
 * @param {Function} calculateBCare - prop function for billable care calculation
 * @param {Function} isMatchingPractice - (practiceKey, practiceObj, item) => boolean
 * @returns {Array} rows with `data` narrowed to matching practices and totals recomputed
 */
export const scopeRowsByPractice = (data, calculateBCare, isMatchingPractice) => {
    return (data || []).reduce((acc, item) => {
        const matchingPracticeKeys = Object.keys(item?.data || {}).filter(practiceKey =>
            isMatchingPractice(practiceKey, item.data[practiceKey], item)
        );
        if (matchingPracticeKeys.length === 0) {
            return acc;
        }
        // Doctor's practices already fully within the filter — nothing to recalculate.
        if (matchingPracticeKeys.length === Object.keys(item.data).length) {
            acc.push(item);
            return acc;
        }
        // Doctor also worked elsewhere — recompute totals from only the matching
        // practices so the row/totals bar don't include pay from other practices.
        const matchingData = {};
        const dataAdjustment = {};
        const dataWriteOff = {};
        matchingPracticeKeys.forEach(practice => {
            matchingData[practice] = item.data[practice];
            if (item.dataAdjustment?.[practice]) dataAdjustment[practice] = item.dataAdjustment[practice];
            if (item.dataWriteOff?.[practice]) dataWriteOff[practice] = item.dataWriteOff[practice];
        });
        // recalculateFromPractices doesn't scope these — the hover popups on the
        // totals row (write-offs/adjustments/financial notes) read them directly, so
        // they'd otherwise still show other-practice entries under the active filter.
        const financialNoteList = (item.financialNoteList || []).filter(note =>
            matchingPracticeKeys.includes(note.practice)
        );
        const recalculated = recalculateFromPractices(item, matchingData, calculateBCare);
        acc.push({
            ...recalculated,
            data: matchingData,
            doctorPracticesArray: matchingPracticeKeys,
            dataAdjustment,
            dataWriteOff,
            financialNoteList,
        });
        return acc;
    }, []);
};

/**
 * Flattens, maps, merges and groups financial data (bCare/write-off/adjustment)
 * into a ProcDate-keyed object. Pure function — no component state required.
 */
export const processFinancialData = (data, priceField, multiplicant = 1) => {
    if (isBlankNullOrUndefined(data)) {
        return {};
    }

    let flatData = [];
    const reportData = data.reportData || data;

    Object.values(reportData).forEach(items => {
        if (Array.isArray(items)) {
            flatData.push(...items);
        }
    });

    const mappedData = flatData.map(item => ({
        ...item,
        price: item[priceField] * multiplicant,
        dateOfService: item.ProcDate,
        date: item.ProcDate,
        patientId: item.PatNum,
        practiceName: item.Practice,
        practice: item.Practice,
    }));

    const mergedData = mergeObjects(mappedData);
    return groupBy(mergedData, 'DateEntry');
};

export const renderTotalPayLabel = (row, calendarRedirectUrl, useAccountFilteredPay = false) => {
    const config = contractLabelConfig[row.contractType];
    if (!config) return null;

    const props = {};
    if (config.color) props.color = config.color;
    if (config.pointing && +row.numberOfWorkedDays && row.displayBasePay > 0) props.pointing = config.pointing(row);
    if (config.className) props.className = typeof config.className === 'function' ? config.className(row) : config.className;
    if (config.style) props.style = config.style;

    const payValue = config.useFinancialNotes
        ? (row.financialNotes || 0)
        : parseFloat((useAccountFilteredPay ? row.accountProcBasedPay : row.totalPay) || 0);

    return (
        <Label {...props} className={props.className + ' d-flex'}>
            <span onClick={() => window.open(`${window.location.origin}${calendarRedirectUrl}`, '_blank')}>{formatCurrency(payValue)}</span>
            {row.payEstimated &&
                <Popup
                    trigger={<Icon name='calculator' className='ml-1' />}
                    content='Estimated pay — re-imagined from Dentist/Oral Surgeon days only, not an official figure.'
                />
            }
            {row.roleBCareFiltered && !row.payEstimated &&
                <Popup
                    trigger={<Icon name='info circle' className='ml-1' />}
                    content={row.isProviderOnTierBonus
                        ? 'Reflects the full period — Tier Bonus pay can\'t be re-estimated from a day subset yet.'
                        : 'Reflects the full period — not narrowed by the Dentist/Oral Surgeon day filter.'}
                />
            }
            <TeamPopUp teamContribution={row.teamContribution} />
        </Label>
    );
};

// Aliases matched against both the structured `speciality` column and the free-text
// `shiftName` — Dentist/Oral Surgeon has historically been driven by shiftName (see the
// older getOralSurgeryPractices), while speciality is mainly used for GA/TED keywords, so
// either signal identifying a role counts.
const ROLE_SPECIALITY_ALIASES = {
    'Oral Surgeon': ['oral surgeon', 'oral surg', 'os'],
    'Dentist': ['dentist', 'general dentist', 'general dentistry', 'gp', 'general'],
    'Pediatric': ['pediatric', 'pediatrics', 'peds', 'pedo'],
};
/**
 * Resolves the role(s) a single shift represents for role-by-day filtering, checking both the
 * `speciality` column and the free-text `shiftName` — either one matching is enough, since a
 * shift's speciality may be populated with an unrelated keyword (e.g. GA/TED) while shiftName
 * still clearly names the role. Returns an empty array when neither identifies a known role
 * ("Not Specified" — can't tell which role this shift/day was worked under).
 */
const resolveShiftRoleLabels = (shift) => {
    const speciality = (shift.speciality || '').trim().toLowerCase();
    const shiftName = (shift.shiftName || '').trim().toLowerCase();
    const labels = Object.entries(ROLE_SPECIALITY_ALIASES)
        .filter(([, aliases]) => {
            const matchesSpeciality = speciality && aliases.some(alias => speciality === alias || speciality.includes(alias));
            const matchesShiftName = shiftName && aliases.some(alias => shiftName.includes(alias));
            return matchesSpeciality || matchesShiftName;
        })
        .map(([role]) => role);
    // Oral Surgeon has always taken precedence over Dentist when a shift matches both.
    return labels.includes('Oral Surgeon') ? labels.filter(l => l !== 'Dentist') : labels;
};

/**
 * Narrows a practice's daily data down to only the days a provider's schedule shows them
 * working the target UDA role (Dentist / Oral Surgeon), and recalculates the bCare-driving
 * inputs (price, adjustment, write-off, visits, HYN care) from just those days.
 *
 * Base Pay / Total Pay are intentionally left at their whole-period values — daily-rate and
 * threshold-based pay categories (see doctorPayHelper.calculateDailyRateBasedPayByCategory)
 * can't be safely re-derived from a day subset on the frontend, so those columns keep
 * reflecting the full period (`basePayNotDayFiltered` flags this for the UI).
 *
 * Returns null when there's no shift data at all for this practice/provider in the period —
 * with nothing to determine role-by-day from, the practice is excluded rather than guessed at.
 *
 * @param {object} practiceData - the practice object with daily data keyed by date
 * @param {array} shifts - array of shift objects from scheduleHours[practiceName]
 * @param {string} providerAbbr - provider abbreviation (lowercase)
 * @param {string|string[]} targetRole - the role(s) to filter for ('Dentist' | 'Oral Surgeon' | 'Pediatric');
 *   a day qualifies when its shift matches any of them
 * @returns {object|null} practice data narrowed to qualifying days, or null to exclude
 */
export const filterPracticeCareByRole = (practiceData, shifts, providerAbbr, targetRole) => {
    if (!practiceData || !targetRole) return practiceData;
    if (!shifts || shifts.length === 0) return null;
    const targetRoles = [].concat(targetRole);

    const abbr = (providerAbbr || '').trim().toLowerCase();
    const dailyData = practiceData.data || {};
    const qualifyingDates = [];
    const dayTags = {};

    Object.keys(dailyData).forEach(dateKey => {
        const shiftsForDay = shifts.filter(shift => {
            if (!shift.startDate || !shift.abbr) return false;
            const shiftDate = typeof shift.startDate === 'string'
                ? shift.startDate.split('T')[0]
                : new Date(shift.startDate).toISOString().split('T')[0];
            if (shiftDate !== dateKey) return false;
            return shift.abbr.toLowerCase().split(';').map(a => a.trim()).includes(abbr);
        });

        if (shiftsForDay.length === 0) {
            dayTags[dateKey] = 'Not Specified';
            return;
        }

        const roleLabels = shiftsForDay.flatMap(resolveShiftRoleLabels);
        const matchedRole = targetRoles.find(r => roleLabels.includes(r));
        if (matchedRole) {
            qualifyingDates.push(dateKey);
            dayTags[dateKey] = matchedRole;
        } else {
            dayTags[dateKey] = roleLabels[0] || 'Not Specified';
        }
    });

    const filteredDailyData = {};
    qualifyingDates.forEach(dateKey => { filteredDailyData[dateKey] = dailyData[dateKey]; });

    const sumProp = (prop) => qualifyingDates.reduce((sum, dateKey) =>
        sum + (parseFloat(dailyData[dateKey]?.[prop]) || 0), 0);

    // Scheduled Days is shift-based, not production-based — count every distinct date this
    // provider was scheduled under the target role at this practice, whether or not that day
    // has any production (mirrors how Worked Days below is production-based, per qualifyingDates).
    const scheduledDatesForRole = new Set();
    shifts.forEach(shift => {
        if (!shift.startDate || !shift.abbr) return;
        const shiftAbbrList = shift.abbr.toLowerCase().split(';').map(a => a.trim());
        if (!shiftAbbrList.includes(abbr)) return;
        if (!resolveShiftRoleLabels(shift).some(r => targetRoles.includes(r))) return;
        const shiftDate = typeof shift.startDate === 'string'
            ? shift.startDate.split('T')[0]
            : new Date(shift.startDate).toISOString().split('T')[0];
        scheduledDatesForRole.add(shiftDate);
    });

    return {
        ...practiceData,
        data: filteredDailyData,
        price: sumProp('price'),
        adjustmentPrice: sumProp('adjustmentPrice'),
        writeOffPrice: sumProp('writeOffPrice'),
        patientVisits: sumProp('patientVisits'),
        billableNotes: sumProp('billableNotes'),
        hynBillableCare: sumProp('hynBillableCare'),
        worked: qualifyingDates.length,
        scheduled: scheduledDatesForRole.size,
        roleDayTags: dayTags,
        basePayNotDayFiltered: true,
    };
};
